import { DefaultFilenameTemplateItemType } from '../../..'
import { NamedComplexEnumItem } from '../../../utils/complexEnum/NamedComplexEnumItem'
import { FilenameTemplateItemCompatible } from '../../filenameTemplateItems/classes/FilenameTemplateItemCompatible'
import { ClientProjectNumber } from '../../filenameTemplateItems/instances/project'

export abstract class ReportItem<
  ID extends string,
  Name extends string,
  ClassType extends string,
  ClassTypeName extends string,
  Endpoint extends string,
  QueryParameter extends string,
  Label extends string,
  SampleFilename extends string,
  ToURLOptions extends ToCommonURLOptions,
  Key extends 'id' | 'globalId'
> extends FilenameTemplateItemCompatible<ID> {
  /**
   * the human readable name for this report
   *
   * some reports may have the same name, but be accessed from different endpoints
   *
   * this is the same value as the old, deprecated enum
   *
   * @see {@link NamedComplexEnumItem}
   */
  readonly name: NamedComplexEnumItem<ID, Name>['name']
  /**
   * the type associated with the `globalId` query parameter passed in a request
   */
  readonly classType: ClassType
  /**
   * the human-readable name of {@link classType}
   */
  readonly classTypeName: ClassTypeName
  /**
   * the endpoint comes after `/api/projects/:projectId/pdf` in the request url
   *
   * this value does not include a leading `/`
   *
   * @example
   * const { endpoint } = Report.Inspection
   * const url = `/api/projects/${projectId}/${endpoint}`
   */
  readonly endpoint: Endpoint
  /**
   * the query parameter to distinguish which report this is for the api
   */
  readonly queryParameter: QueryParameter
  /**
   * a key of {@link classType} describing which label should be used in the UI
   *
   * @example 'asphaltLotId'
   */
  readonly label: Label
  /**
   * whether this report can be run from the client
   */
  readonly accessibleInClient: boolean
  /**
   * the value of the sample's filename on the server
   */
  readonly sampleFilename?: SampleFilename
  readonly defaultTemplateItems: DefaultFilenameTemplateItemType[]
  /**
   * the primary key associated with the {@link classType}
   */
  readonly key: Key
  /**
   * @see {@link FilenameTemplateItemCompatible}
   */
  constructor({
    id,
    accessibleInClient,
    classType,
    classTypeName,
    endpoint,
    label,
    name,
    queryParameter,
    sampleFilename,
    afterClientProjectNumber,
    compatibleTemplateItems,
    key,
  }: ReportItemConstructor<
    ID,
    Name,
    ClassType,
    ClassTypeName,
    Endpoint,
    QueryParameter,
    Label,
    SampleFilename,
    Key,
    ToURLOptions
  >) {
    super({ id, compatibleTemplateItems })
    this.accessibleInClient = accessibleInClient ?? true
    this.classType = classType
    this.classTypeName = classTypeName
    this.endpoint = endpoint
    this.label = label
    this.name = name
    this.queryParameter = queryParameter
    this.sampleFilename = sampleFilename
    this.defaultTemplateItems = [
      { type: 'templateItem', templateItemId: ClientProjectNumber.id },
      ...afterClientProjectNumber,
    ]
    this.key = key
  }
  /**
   * returns a url string with all possible search params set
   *
   * @param options {@link ToURLOptions}
   * @see {@link __toURLObject}
   * @see {@link SimpleURLOptions}
   */
  abstract toURL(options: ToURLOptions): string
  /**
   * constructs the url and adds the `report` search parameter, returning the
   * constructed url
   *
   * @param options {@link ToCommonURLOptions}
   */
  protected __toURLObject({ baseURL, projectId }: ToCommonURLOptions) {
    const url = new URL(
      `${baseURL}/api/projects/${projectId}/pdf/${this.endpoint}`
    )
    url.searchParams.set('report', this.queryParameter)
    return url
  }
  /**
   * adds a search parameter for `globalId`
   *
   * @param url the url generated from {@link __toURLObject}
   * @param globalId
   */
  protected __addGlobalId(url: URL, globalId: string) {
    url.searchParams.set('globalId', globalId)
  }
}

export type ReportItemConstructor<
  ID extends string,
  Name extends string,
  ClassType extends string,
  ClassTypeName extends string,
  Endpoint extends string,
  QueryParameter extends string,
  Label extends string,
  SampleFilename extends string,
  Key extends 'id' | 'globalId',
  ToURLOptions extends ToCommonURLOptions = SimpleURLOptions
> = Pick<
  ReportItem<
    ID,
    Name,
    ClassType,
    ClassTypeName,
    Endpoint,
    QueryParameter,
    Label,
    SampleFilename,
    ToURLOptions,
    Key
  >,
  | 'id'
  | 'classType'
  | 'classTypeName'
  | 'endpoint'
  | 'label'
  | 'name'
  | 'queryParameter'
  | 'sampleFilename'
  | 'compatibleTemplateItems'
  | 'key'
> & {
  accessibleInClient?: false
  /**
   * template items to come after {@link ClientProjectNumber}
   */
  afterClientProjectNumber: DefaultFilenameTemplateItemType[]
}

export type ToCommonURLOptions = {
  /**
   * the base URL associated with the environment
   *
   * comes before /api/projects/...
   *
   * @example 'http://localhost:5000'
   * @example 'https://cims.horrocks.com/reports'
   */
  baseURL: string | undefined
  /**
   * the project to request the report for
   */
  projectId: string
}

export type SimpleURLOptions = ToCommonURLOptions & {
  /**
   * the globalId of the record associated with the report
   */
  globalId: string
}
